/**
 * useAdminLoginFeed — admin-only Socket.IO subscription to live login events.
 *
 *   const { connected } = useAdminLoginFeed({ token, enabled, onEvent });
 *
 * - Joins the backend `admins` room via `join_admin` (validates JWT + role).
 * - Receives `login:event` payloads pushed by `broadcast_login_event` after
 *   each login attempt (success and failed). Calls `onEvent(payload)`.
 * - Toggle on/off without unmounting the consumer: pass `enabled=false` and
 *   the hook leaves the room without disconnecting the shared socket.
 *
 * Reuses the shared socket from the ticket chat — there's only one socket
 * per tab regardless of how many features subscribe.
 */
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

let sharedSocket = null;
let sharedRefCount = 0;

const origin = () => (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');

function acquireSocket() {
  if (sharedSocket) { sharedRefCount += 1; return sharedSocket; }
  sharedSocket = io(origin(), {
    path: '/api/socket.io',
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15000,
    timeout: 20000,
  });
  sharedRefCount = 1;
  return sharedSocket;
}

function releaseSocket() {
  sharedRefCount = Math.max(0, sharedRefCount - 1);
  if (sharedRefCount === 0 && sharedSocket) {
    try { sharedSocket.disconnect(); } catch (_) { /* noop */ }
    sharedSocket = null;
  }
}

export default function useAdminLoginFeed({ token, enabled = true, onEvent, onOrder, onRecharge, onTicketMessage } = {}) {
  const [connected, setConnected] = useState(false);
  const handlerRef = useRef({ onEvent, onOrder, onRecharge, onTicketMessage });
  handlerRef.current = { onEvent, onOrder, onRecharge, onTicketMessage };

  useEffect(() => {
    if (!enabled || !token) return undefined;

    const socket = acquireSocket();

    const joinAdmin = () => {
      socket.emit('join_admin', { token }, (ack) => {
        if (ack && ack.ok) setConnected(true);
        else setConnected(false);
      });
    };

    const onConnect = () => joinAdmin();
    const onDisconnect = () => setConnected(false);
    const onLoginEvent = (payload) => {
      try { handlerRef.current.onEvent && handlerRef.current.onEvent(payload); } catch (_) { /* noop */ }
    };
    const onOrderEvent = (payload) => {
      try { handlerRef.current.onOrder && handlerRef.current.onOrder(payload); } catch (_) { /* noop */ }
    };
    const onRechargeEvent = (payload) => {
      try { handlerRef.current.onRecharge && handlerRef.current.onRecharge(payload); } catch (_) { /* noop */ }
    };
    const onTicketEvent = (payload) => {
      try { handlerRef.current.onTicketMessage && handlerRef.current.onTicketMessage(payload); } catch (_) { /* noop */ }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('login:event',         onLoginEvent);
    socket.on('order:new',           onOrderEvent);
    socket.on('recharge:new',        onRechargeEvent);
    socket.on('ticket:new_message',  onTicketEvent);
    if (socket.connected) joinAdmin();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('login:event',         onLoginEvent);
      socket.off('order:new',           onOrderEvent);
      socket.off('recharge:new',        onRechargeEvent);
      socket.off('ticket:new_message',  onTicketEvent);
      try { socket.emit('leave_admin', {}); } catch (_) { /* noop */ }
      releaseSocket();
    };
  }, [token, enabled]);

  return { connected };
}
